/**
 * Builds the aggregates payload for POST /api/ai/category-analysis. Shared by
 * CategoryAnalysisDialog and the category card's InsightsPanel so both hit the
 * same backend cache entry (the cache is keyed on a hash of this payload).
 * Sends only client-computed aggregates — never raw transactions.
 */

// Cap what we send: two years of months and a handful of description
// aggregates keep the payload ~1-3 KB, far under the backend's 20 KB guard.
const MAX_MONTHS = 24;
const MAX_DESCRIPTIONS = 8;

const round2 = (n) => Math.round(n * 100) / 100;

export default function buildCategoryAnalysisPayload(category, budgetData, transactions) {
  if (!category || !budgetData) return null;
  const incomeSeries = budgetData.incomeCategories[category.id] || {};
  const expenseSeries = budgetData.expenseCategories[category.id] || {};
  const hasIncome = Object.values(incomeSeries).some((v) => v !== 0);
  const hasExpense = Object.values(expenseSeries).some((v) => v !== 0);
  const amountMeaning = hasIncome && hasExpense ? 'net' : hasIncome ? 'income' : 'spend';

  const amountFor = (month) => {
    const income = incomeSeries[month] || 0;
    const expense = expenseSeries[month] || 0; // stored positive
    if (amountMeaning === 'income') return income;
    if (amountMeaning === 'spend') return expense;
    return income - expense;
  };

  let months = budgetData.months.map((month) => ({ month, amount: round2(amountFor(month)) }));
  const firstActive = months.findIndex((m) => m.amount !== 0);
  if (firstActive === -1) return { noData: true };
  months = months.slice(firstActive).slice(-MAX_MONTHS);

  const active = months.filter((m) => m.amount !== 0);
  const total = round2(months.reduce((sum, m) => sum + m.amount, 0));
  const byAmount = [...active].sort((a, b) => a.amount - b.amount);
  const last = months[months.length - 1];
  const prior = months[months.length - 2];
  const lastMonthChangePct =
    prior && prior.amount !== 0
      ? round2(((last.amount - prior.amount) / Math.abs(prior.amount)) * 100)
      : null;

  // Top recurring descriptions within the covered window, by absolute total.
  const monthSet = new Set(months.map((m) => m.month));
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const byDescription = {};
  (transactions || []).forEach((t) => {
    if (t.category !== category.id) return;
    const d = new Date(t.date);
    const month = `${monthNames[d.getUTCMonth()]}-${d.getUTCFullYear().toString().substr(2, 2)}`;
    if (!monthSet.has(month)) return;
    const description = String(t.description || '').trim().slice(0, 60);
    if (!description) return;
    if (!byDescription[description]) byDescription[description] = { description, count: 0, total: 0 };
    byDescription[description].count += 1;
    byDescription[description].total += t.amount;
  });
  const topDescriptions = Object.values(byDescription)
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))
    .slice(0, MAX_DESCRIPTIONS)
    .map((d) => ({ ...d, total: round2(d.total) }));

  return {
    category: { name: category.name, type: category.type },
    currency: 'NZD',
    amountMeaning,
    months,
    stats: {
      total,
      monthlyAverage: round2(total / months.length),
      activeMonths: active.length,
      min: byAmount[0] || null,
      max: byAmount[byAmount.length - 1] || null,
      lastMonthChangePct,
    },
    topDescriptions,
  };
}
